// Shared loading / failure states for the caregiver screens.
//
// These exist because of a confirmed live-only defect: Home, Child detail and
// Progress export read `linked` from useLinkedChildren but ignored both its
// `loading` flag and (until now) the fact that a FAILED fetch also produced an
// empty array. So on the live site, during every page load and after every
// error, a parent with linked children was shown the confident, calm message
// "You haven't linked any children yet" — and, on Child detail, "This child is
// not linked to your account."
//
// Wrong-and-calm is worse than slow. A parent who reads that reasonably
// concludes their child's data is gone.

/** Shown while the first fetch is in flight. Deliberately quiet — no skeleton
 *  shimmer, which reads as agitation on a screen families use to check in. */
export function LoadingChildren({ label = "Loading your children…" }: { label?: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{ display: "flex", alignItems: "center", gap: 12, padding: "28px 0" }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 20,
          height: 20,
          border: "3px solid var(--line-strong)",
          borderTopColor: "var(--brand)",
          borderRadius: "50%",
          animation: "zspin .8s linear infinite",
          flex: "none",
        }}
      />
      <span style={{ color: "var(--ink-soft)", fontSize: 15.5 }}>{label}</span>
    </div>
  );
}

/**
 * Shown when the fetch failed. Warm peach, never red (05_PORTAL_DESIGN.md §3):
 * a load failure is not the family's fault and must not read like an alarm.
 *
 * The retry is the important part — without it the only recovery is a full page
 * reload, which also drops the session restore.
 */
export function ChildrenLoadError({
  message,
  onRetry,
}: {
  message?: string;
  onRetry: () => void;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        padding: "18px 20px",
        background: "var(--peach-tint)",
        borderRadius: "var(--r-lg)",
        maxWidth: 560,
      }}
    >
      <b style={{ display: "block", color: "var(--peach-deep)", fontSize: 16.5 }}>
        We couldn't load your children just now
      </b>
      <p style={{ color: "var(--ink-soft)", fontSize: 15.5, lineHeight: 1.6, margin: "8px 0 0" }}>
        Nothing has changed on your account — this is almost always a connection
        hiccup.
      </p>
      {message ? (
        <p
          style={{
            color: "var(--ink-faint)",
            fontSize: 13,
            margin: "8px 0 0",
            wordBreak: "break-word",
          }}
        >
          {message}
        </p>
      ) : null}
      <button
        onClick={onRetry}
        style={{
          marginTop: 14,
          minHeight: 46,
          padding: "0 22px",
          border: "none",
          background: "var(--brand)",
          color: "#fff",
          borderRadius: "var(--r-pill)",
          fontFamily: "var(--font-body)",
          fontWeight: 700,
          fontSize: 15.5,
          cursor: "pointer",
        }}
      >
        Try again
      </button>
    </div>
  );
}
